import { Autocomplete, FormControl, Stack, TextField } from "@mui/material";
import { FormEvent, SyntheticEvent, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useAddMedicationStrengthsMedicationsMutation,
  useGetStrengthsQuery,
} from "src/api/services/medicationStrengthsApi";
import { MedicationStrength } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  onClose: () => void;
}

const ExistingStrengthForm = ({ onClose }: Props) => {
  const { medicationId } = useParams<MatchParam<"MEDICATION_ID">>();
  const { data: strengths = [] } = useGetStrengthsQuery();
  const [addMedicationStrength] =
    useAddMedicationStrengthsMedicationsMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [strength, setStrength] = useState<MedicationStrength | null>(null);
  const errLog = useSentryLogger("error");

  const handleStrength = useCallback(
    (_: SyntheticEvent<Element | Event>, value: MedicationStrength | null) => {
      setStrength(value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (medicationId == null || strength == null) return;
      setLoading(true);

      try {
        await addMedicationStrength({
          medication_id: Number(medicationId),
          strength_id: strength.id,
        }).unwrap();
      } catch (error) {
        errLog("Unable to add medication strength to medication", error);
      } finally {
        setStrength(null);
        setLoading(false);
        onClose();
      }
    },
    [addMedicationStrength, errLog, medicationId, onClose, strength],
  );

  return (
    <Stack component={"form"} onSubmit={handleSubmit} gap={2}>
      <FormControl>
        <Autocomplete
          options={strengths}
          getOptionLabel={(s) =>
            `${s.strength_value} ${s.strength_unit} / ${s.reference_amount} ${s.reference_unit}`
          }
          renderInput={(params) => (
            <TextField {...params} label="Strength (formula)" required />
          )}
          value={strength}
          onChange={handleStrength}
        />
      </FormControl>
      <LoadingButton type="submit" loading={loading}>
        Add
      </LoadingButton>
    </Stack>
  );
};

export default ExistingStrengthForm;
